import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Pagination, useTheme, useMediaQuery } from "@mui/material";
import { getCustomerParcels } from "api/financial";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import { useSetBreadcrumb } from "hooks/BreadcrumbContext";

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : "");
const initials = (s) => (s || "?").slice(0, 2).toUpperCase();
const labelSx = { fontSize: "10.5px", letterSpacing: ".14em", color: "#a8a3b4", fontWeight: 700 };
const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: ".1em", color: "#a8a3b4", textTransform: "uppercase" };
const GRID = "1.4fr 2fr 1.1fr 1.1fr 0.9fr";

export default function OutstandingParcels() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { stationId, customerId } = useParams();

  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [summary, setSummary] = useState({ count: 0, totalFee: 0, totalCod: 0, pendingCount: 0 });
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [tracking, setTracking] = useState(null);

  const load = useCallback(async () => {
    const res = await getCustomerParcels(stationId, customerId, { page, limit: 50 });
    setRows(res && res.success ? res.data : []);
    setMeta((res && res.pagination) || { pages: 1, total: 0 });
    setSummary((res && res.summary) || { count: 0, totalFee: 0, totalCod: 0, pendingCount: 0 });
    if (res?.customer) setName(res.customer.name);
  }, [stationId, customerId, page]);
  useEffect(() => { load(); }, [load]);

  useSetBreadcrumb([
    { label: t("financial.title"), to: "/financial" },
    { label: `${t("financial.outstandingByStation")} · ${stationId}`, to: `/financial/outstanding/${encodeURIComponent(stationId)}` },
    { label: name || customerId },
  ]);

  const StatusChip = ({ pending }) => pending
    ? <Box sx={{ display: "inline-block", fontSize: "10.5px", fontWeight: 700, color: "#c0851a", background: "#fbf0d9", padding: "3px 9px", borderRadius: "999px" }}>{t("financial.pending")}</Box>
    : <Box sx={{ display: "inline-block", fontSize: "10.5px", fontWeight: 700, color: "#6d28d9", background: "#f3f0fc", padding: "3px 9px", borderRadius: "999px" }}>{t("financial.unpaid")}</Box>;

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1280, mr: "auto", p: { xs: "20px", md: "28px 40px 60px" } }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: "linear-gradient(135deg,#7c3aed,#4c1d95)", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{initials(name)}</Box>
        <Box>
          <Box component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 800, color: "#16131f", letterSpacing: "-.01em" }}>{name || customerId}</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "5px", flexWrap: "wrap" }}>
            <Box sx={{ fontSize: "11.5px", fontWeight: 700, color: "#6d28d9", background: "#f3f0fc", border: "1px solid #ddd0fa", padding: "3px 10px", borderRadius: "999px" }}>{stationId}</Box>
            <Box sx={{ fontSize: "11.5px", fontWeight: 700, color: "#2c2937", background: "#f1eff5", padding: "3px 10px", borderRadius: "999px" }}>{summary.count} {t("financial.parcels")}</Box>
          </Box>
        </Box>
      </Box>

      {/* KPI strip */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: "16px", mt: "22px" }}>
        <Box sx={{ background: "linear-gradient(155deg,#fff 0%,#f5efff 100%)", border: "1px solid #ece4fb", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)" }}>
          <Box sx={labelSx}>{t("financial.parcels").toUpperCase()}</Box>
          <Box sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: "#161420", mt: "8px" }}>{summary.count}</Box>
        </Box>
        <Kpi label={t("financial.totalOutstanding")} value={`฿${fmt(summary.totalFee)}`} color="#6d28d9" />
        <Kpi label={t("financial.totalCod")} value={`฿${fmt(summary.totalCod)}`} />
        <Kpi label={t("financial.pending")} value={fmt(summary.pendingCount)} color={summary.pendingCount > 0 ? "#c0851a" : "#161420"} />
      </Box>

      {/* list */}
      {rows.length === 0 ? (
        <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", mt: "16px", p: "50px 24px", textAlign: "center", color: "#b9b4c4" }}>{t("financial.noData")}</Box>
      ) : isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", mt: "16px" }}>
          {rows.map((r) => (
            <Box key={r._id} onClick={() => setTracking(r.tracking)} sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "16px", p: "14px 16px", cursor: "pointer" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                <Box sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: "#5b21b6" }}>{r.tracking}</Box>
                <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: "#161420" }}>฿{fmt(r.shippingFee)}</Box>
              </Box>
              {r.description && <Box sx={{ fontSize: 12, color: "#6b6775", mt: "4px" }}>{r.description}</Box>}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "8px" }}>
                <Box sx={{ fontSize: 11.5, color: "#9b96a6" }}>{fmtD(r.createdAt)}</Box>
                <StatusChip pending={!!r.pendingShippingPaymentId} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", overflow: "hidden", mt: "16px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 16px 40px -28px rgba(20,18,30,.18)" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", p: "13px 24px", background: "#faf9fc", borderBottom: "1.5px solid #ece9f1" }}>
            <Box sx={headCell}>{t("financial.tracking")}</Box>
            <Box sx={headCell}>{t("financial.description")}</Box>
            <Box sx={{ ...headCell, color: "#6d28d9", textAlign: "right" }}>{t("financial.shippingFeeTHB")}</Box>
            <Box sx={{ ...headCell, textAlign: "center" }}>{t("financial.date")}</Box>
            <Box sx={{ ...headCell, textAlign: "right" }}>{t("financial.status")}</Box>
          </Box>
          {rows.map((r) => (
            <Box key={r._id} onClick={() => setTracking(r.tracking)}
              sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", p: "0 24px", borderTop: "1px solid #f4f2f8", minHeight: 58, cursor: "pointer", "&:hover": { background: "#faf9fc" } }}>
              <Box sx={{ py: "12px", fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#5b21b6" }}>{r.tracking}</Box>
              <Box sx={{ fontSize: 13, color: "#6b6775", pr: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.description || "—"}</Box>
              <Box sx={{ textAlign: "right", fontFamily: MONO, fontSize: 15, fontWeight: 700, color: "#161420" }}>฿{fmt(r.shippingFee)}</Box>
              <Box sx={{ textAlign: "center", fontFamily: MONO, fontSize: 12.5, color: "#6b6775" }}>{fmtD(r.createdAt)}</Box>
              <Box sx={{ textAlign: "right" }}><StatusChip pending={!!r.pendingShippingPaymentId} /></Box>
            </Box>
          ))}
        </Box>
      )}

      {meta.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
          <Pagination color="secondary" count={meta.pages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}

      <TrackingDetailDialog open={!!tracking} trackingNumber={tracking} onClose={() => setTracking(null)} />
    </Box>
  );
}

function Kpi({ label, value, color }) {
  return (
    <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(20,18,30,.12)" }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: color || "#161420", mt: "8px" }}>{value}</Box>
    </Box>
  );
}
